import React, { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import {
  ActionFocus,
  DuplicateOrReschedulePostHandler,
  PostPauseStatusType,
  PostPreviewType,
  PostScheduleViewContext,
  QueueModalPackageType
} from './PostSchedule.controller';
import { PostScheduleWeek } from './PostScheduleWeek';
import { useThirdNavChildren } from '../../../hooks/useThirdNavChildren';
import { ThirdNavLeftButtons, ThirdNavRightButtons } from './ThirdLevelNavigation';
import { PostScheduleList } from './PostScheduleList';
import { PostScheduleLibrary } from './PostScheduleLibrary';

import {
  CoreCategoryFieldsFragment,
  PostItemFragment,
  PostOrderUpdateDirection,
  PostQueuePosition,
  QueueFilterPostStatus,
  QueueFilterPostTiming,
  SocialProfileFieldsFragment,
  StartOfWeekFormatSetting
} from '../../../models/graphql';
import { CardOrientationEnum } from '../../../components/cards/BasePostCard/BasePostCard.view';
import { ThirdNavLibraryFilters } from './ThirdLevelNavigation/ThirdLevelNavigation.view';
import { PostScheduleFilterAction, PostScheduleFilterType, QueueFiltersType } from '../../../models/post';
import { SubmitHandler, UseFormReturn } from 'react-hook-form/dist/types/form';
import { DeletePostModalView } from './DeletePostModal/DeletePostModal.view';
import { PauseModalView } from './PauseModal/PauseModal.view';
import { ShuffleModal } from './ShuffleModal';
import { FloatingActionBar } from '../../../components/containers/FloatingActionBar';
import { FloatingActionBoxModeEnum } from '../../../components/containers/FloatingActionBar/FloatingActionBar.view';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { PostScheduleFilterMenu } from './PostScheduleFilterMenu';
import { Modal } from '../../../components/containers/Modal';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { ContainerListItem } from '../../../components/actions/ListItem/ListItem.view';
import { PostScheduleContext } from '../../../providers/PostScheduleProvider';
import { QueueCountContext } from '../../../providers/QueueCountProvider';
import { CreateEditCategoryModal } from '../Categories/CreateEditCategoryModal';
import { GetCategories_workspace_categories } from '../../../models/legacy-graphql';
import { PostPreviewModal } from './PostPreviewModal';

export type PostScheduleViewProps = {
  loadingMore?: boolean;
  postIterations: PostItemFragment[];
  categories: CoreCategoryFieldsFragment[];
  socialProfiles: SocialProfileFieldsFragment[];
  onSubmit: SubmitHandler<{ term: string }>;
  formInstance: UseFormReturn;
  filters: PostScheduleFilterType;
  dispatchFilters: Dispatch<PostScheduleFilterAction>;
  startDate: Date;
  endDate: Date;
  hideFilters: boolean;
  forceListView: boolean;
  width: number;
  setSearchTerm: (term: string) => void;
  setDateRange: (amount: number) => void;
  orientation: CardOrientationEnum;
  setOrientation: (orientation: CardOrientationEnum) => void;
  selectedCategory: GetCategories_workspace_categories | undefined;
  context: PostScheduleViewContext;
  setContext: (context: PostScheduleViewContext) => void;
  showCategoryEditModal: boolean;
  setShowCategoryEditModal: (show: boolean) => void;
  showDetailsModalHandler: (postID: string) => void;
  actOnAllPosts: (callback: CallableFunction) => void;
  actionFocus: ActionFocus;
  setActionFocus: (mode: ActionFocus) => void;
  deletePosts: (postIds: string[], action: 'Trash' | 'Delete' | 'Restore') => void;
  pauseAllHandler: (pause: boolean) => void;
  shufflePostsHandler: () => void;
  updatePostLocation: (postId: string, refPostId: string, direction: PostOrderUpdateDirection) => Promise<void>;
  movePostPosition: (postId: string, position: PostQueuePosition) => void;
  getActionMenuListItems: (post: PostItemFragment) => ContainerListItem[];
  queueFilters: QueueFiltersType;
  isQueuePaused: boolean;
  categoryPauseStatus: PostPauseStatusType[];
  queueModals: QueueModalPackageType;
  dayStartOfWeek: StartOfWeekFormatSetting;
  duplicateOrReschedulePost: DuplicateOrReschedulePostHandler;
  wsTz: string;
  showPostPreview: null | PostPreviewType;
  setShowPostPreview: Dispatch<SetStateAction<null | PostPreviewType>>;
  selectedPosts: string[];
  checkboxes: string[];
  deleteSinglePost: (id: string) => void;
  postIdToDelete?: string;
  setPostIdToDelete: (id: string | undefined) => void;
};

export const PostScheduleView = (props: PostScheduleViewProps): JSX.Element => {
  const {
    context,
    forceListView,
    actOnAllPosts,
    queueModals,
    showCategoryEditModal,
    setShowCategoryEditModal,
    selectedCategory,
    showPostPreview,
    setShowPostPreview,
    selectedPosts,
    checkboxes,
    postIdToDelete,
    setPostIdToDelete
  } = props;
  const { loadingInitial, setLoadingInitial, postsCount } = useContext(PostScheduleContext);
  const selectedCount = selectedPosts.length;

  const postIdsForAction =
    !checkboxes || props.context === PostScheduleViewContext.WEEK || props.actionFocus === ActionFocus.ALL
      ? []
      : selectedPosts;

  const deleteType = props.filters.status === QueueFilterPostStatus.DELETED ? 'Delete' : 'Trash';

  const { queueCounts } = useContext(QueueCountContext);
  const topNavChildren = useMemo(() => {
    return props.context !== PostScheduleViewContext.LIBRARY
      ? {
          left: (
            <ThirdNavLeftButtons
              {...props}
              context={props.context}
              setContext={props.setContext}
              setShowFilterMenu={queueModals.filter.setter}
              startDate={props.startDate}
              endDate={props.endDate}
              setDateRange={props.setDateRange}
              isLoading={loadingInitial || false}
              queueCounts={queueCounts}
            />
          ),
          right: (
            <ThirdNavRightButtons
              context={props.context}
              isLoading={loadingInitial || false}
              hasPosts={!!postsCount}
              isDateSetFilter={props.queueFilters.timing === QueueFilterPostTiming.SET_DATES}
              queueFilters={props.filters}
              actOnAllPosts={actOnAllPosts}
              isQueuePaused={props.isQueuePaused}
              deleteLabel={deleteType}
              shuffleAction={() => {
                queueModals.shuffle.setter(true);
              }}
              pauseAction={() => {
                queueModals.pause.setter(true);
              }}
              deleteAction={() => {
                props.setActionFocus(ActionFocus.ALL);
                queueModals.delete.setter(true);
              }}
            />
          )
        }
      : props.width < BreakpointsValuesEnum.medium
      ? {
          left: (
            <ThirdNavLibraryFilters
              {...props}
              setShowFilterMenu={queueModals.filter.setter}
              queueCounts={queueCounts}
            />
          )
        }
      : undefined;
  }, [props, queueModals, loadingInitial, queueCounts, postsCount, actOnAllPosts, deleteType]);

  useThirdNavChildren(topNavChildren);

  return (
    <>
      {(forceListView && context !== PostScheduleViewContext.LIBRARY) || context === PostScheduleViewContext.LIST ? (
        <PostScheduleList {...props} />
      ) : !forceListView && context === PostScheduleViewContext.WEEK ? (
        <PostScheduleWeek {...props} />
      ) : (
        context === PostScheduleViewContext.LIBRARY && <PostScheduleLibrary {...props} />
      )}
      {queueModals.delete.state && (
        <DeletePostModalView
          context={props.context}
          deleteType={deleteType}
          onClose={(confirmed: boolean) => {
            if (confirmed) {
              setLoadingInitial(true);
              const toDelete = postIdToDelete ? [postIdToDelete] : selectedPosts;
              props.deletePosts(toDelete, deleteType);
            } else {
              setPostIdToDelete(undefined);
            }

            queueModals.delete.setter(false);
          }}
          postCount={postIdToDelete ? 1 : props.actionFocus === ActionFocus.ALL ? postsCount : postIdsForAction.length}
        />
      )}
      {queueModals.pause.state && (
        <PauseModalView
          currentState={props.isQueuePaused ? 'PAUSED' : 'UNPAUSED'}
          onClose={() => queueModals.pause.setter(false)}
          onPause={() => props.pauseAllHandler(!props.isQueuePaused)}
        />
      )}
      {queueModals.shuffle.state && (
        <ShuffleModal
          onClose={(confirmed: boolean) => {
            if (confirmed) {
              setLoadingInitial(true);
              props.shufflePostsHandler();
            }
            queueModals.shuffle.setter(false);
          }}
        />
      )}
      {selectedCount > 0 && (
        <FloatingActionBar
          mode={FloatingActionBoxModeEnum.DARK}
          selectedLabel={'Posts selected'}
          itemsSelectedCount={selectedCount}>
          <Button
            type={'button'}
            variant={'hollow'}
            size={'medium'}
            color={ButtonColorsEnum.white}
            icon={{ side: 'left', value: IconsEnum.trash }}
            action={() => {
              props.setActionFocus(ActionFocus.SELECTED);
              queueModals.delete.setter(true);
            }}>
            {deleteType}
          </Button>
        </FloatingActionBar>
      )}
      {queueModals.filter.state && props.width < BreakpointsValuesEnum.medium && (
        <Modal onClose={() => queueModals.filter.setter(false)} showCloseIcon={true} title={'Filters'} isFilter>
          <PostScheduleFilterMenu
            filters={props.filters}
            context={props.context}
            dispatchFilters={props.dispatchFilters}
            categories={props.categories}
            socialProfiles={props.socialProfiles}
            formInstance={props.formInstance}
            onSubmit={props.onSubmit}
            setSearchTerm={props.setSearchTerm}
            queueCounts={queueCounts}
          />
        </Modal>
      )}
      {showCategoryEditModal && (
        <CreateEditCategoryModal onClose={() => setShowCategoryEditModal(false)} category={selectedCategory} />
      )}
      {showPostPreview && <PostPreviewModal onClose={() => setShowPostPreview(null)} {...showPostPreview} />}
    </>
  );
};
